import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarDays, ArrowRight } from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { getEvents } from "@/lib/api";

const EventCard = ({ e, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.4, delay: index * 0.04 }}
    data-testid={`event-card-${e.flight_rcode}-${e.flight_rleg}`}
    className="flex items-center justify-between gap-4 rounded-2xl border border-white/5 bg-[#12141D] p-5 transition-colors duration-300 hover:border-[#D4AF37]/30"
  >
    <div className="min-w-0">
      <p className="font-mono-data text-sm font-bold text-[#D4AF37]">{e.flight_number}</p>
      <div className="mt-1 flex items-center gap-2 text-sm text-[#FDFBF7]">
        <span>{e.dep_icao || e.dep_iata || "—"}</span>
        <ArrowRight className="h-3.5 w-3.5 text-[#A1A1AA]" />
        <span>{e.arr_icao || e.arr_iata || "—"}</span>
      </div>
      <p className="mt-1 truncate text-xs text-[#A1A1AA]">{e.dep_name} → {e.arr_name}</p>
    </div>
    <div className="flex-shrink-0 text-right">
      <p className="text-sm font-semibold text-[#FDFBF7]">{e.date}</p>
      <p className="text-xs text-[#A1A1AA]">{e.time} · {e.time_diff}</p>
    </div>
  </motion.div>
);

const Section = ({ title, items }) => (
  <div className="mt-10">
    <h2 className="font-display text-lg font-bold text-[#FDFBF7]">{title}</h2>
    <div className="mt-4 space-y-3">
      {items.length === 0 ? (
        <p className="py-8 text-center text-sm text-[#A1A1AA]">Nothing here right now.</p>
      ) : (
        items.map((e, i) => <EventCard key={`${e.flight_rcode}-${e.flight_rleg}-${i}`} e={e} index={i} />)
      )}
    </div>
  </div>
);

export default function Events() {
  const [data, setData] = useState({ current: [], upcoming: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getEvents()
      .then((r) => setData(r || { current: [], upcoming: [] }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0B10]" data-testid="events-page">
      <Navbar />
      <main className="mx-auto max-w-3xl px-5 pb-24 pt-28 lg:px-10 lg:pt-32">
        <div className="max-w-2xl">
          <div className="mb-4 h-0.5 w-16 bg-[#8A1538]" />
          <p className="text-overline flex items-center gap-2 text-[#8A1538]">
            <CalendarDays className="h-4 w-4" /> Group Flights
          </p>
          <h1 className="mt-3 font-display text-4xl font-black tracking-tight text-[#FDFBF7] lg:text-5xl [text-wrap:balance]">
            Current &amp; upcoming events
          </h1>
        </div>

        {loading ? (
          <p className="mt-16 text-center text-sm text-[#A1A1AA]">Loading events…</p>
        ) : (
          <>
            <Section title="Happening today" items={data.current} />
            <Section title="Upcoming" items={data.upcoming} />
          </>
        )}
      </main>
    </div>
  );
}
